import { useEffect, useState } from 'react'
import { Form } from 'react-bootstrap'
import { useFormik } from 'formik'
import { Building2, Hash, MapPin, Flag, FileText, UserCheck, UserPlus, AlertCircle } from 'lucide-react'
import { SelectBox } from 'components/input_fields'
import { SaveButton } from 'components/buttons'
import { Loader } from 'components/loader'
import { AssignTicketSchema } from './ValidationSchema'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { ticketDetails, assignTicket } from '../../../../redux/slices/help_desk'
import { premiseUserList, removePremiseUser } from '../../../../redux/slices/users'
import { toggleAssignModal, toggleBool } from '../../../../redux/slices/globalSlice'
import { addUpdateHandler } from 'utils'
import { useTranslation } from 'react-i18next'

const PRIORITY_CLASS = {
    Low: 'badge bg-secondary',
    Medium: 'badge bg-info text-dark',
    High: 'badge bg-warning text-dark',
    Critical: 'badge bg-danger',
}

const STATUS_CLASS = {
    Open: 'badge bg-primary',
    Assigned: 'badge bg-secondary',
    'In Progress': 'badge bg-info text-dark',
    'On Hold': 'badge bg-warning text-dark',
    Resolved: 'badge bg-success',
    Closed: 'badge bg-dark',
    Rejected: 'badge bg-danger',
}

const userDisplay = (u) => {
    if (!u) return null
    const name = [u.first_name, u.last_name].filter(Boolean).join(' ')
    return name || u.user_mobile || null
}

export default function AssignTicketForm() {

    const [loading, setLoading] = useState(true)
    const [breakLoading, setBreakLoading] = useState(true)
    const [spinner, setSpinner] = useState(false)
    const { t } = useTranslation()
    const navigate = useNavigate()
    const dispatch = useDispatch()

    const id = useSelector((state) => state?.global?.assignId)
    const details = useSelector((state) => state?.ticket?.details)
    const users = useSelector((state) => state?.premiseUser?.list)
    const userLoading = useSelector((state) => state?.premiseUser?.loading)

    const user_option = users
        ?.filter((u) => u?.id !== details?.assigned_to)
        ?.map((u) => ({
            value: u?.id,
            label: userDisplay(u) || '—',
            roles: u?.userRoles,
        })) || []

    const userOptionRender = (option) => (
        <div className="ticket-assign-user-option">
            <span className="ticket-assign-user-name">{option.data.label}</span>
            <div className="ticket-assign-user-roles">
                {option.data.roles?.map((role, idx) =>
                    role?.roleInfo?.role_name ? (
                        <span key={idx} className="ticket-assign-user-role-badge">
                            {role.roleInfo.role_name}
                        </span>
                    ) : null
                )}
            </div>
        </div>
    )

    const initialValues = {
        user_id: '',
    }

    const formik = useFormik({
        initialValues: initialValues,
        validationSchema: AssignTicketSchema,
        enableReinitialize: true,

        onSubmit: (values, action) => {
            setSpinner(true)
            dispatch(assignTicket({ ticket_id: details?.id, user_id: values?.user_id })).then((res) =>
                addUpdateHandler({
                    res: res.payload,
                    action,
                    navigate,
                    success_toast: t('ticket.ticket_assigned_success'),
                    wrong_toast: t('common.wrong'),
                    navigate_route: '',
                    spinner: setSpinner,
                    toggle: () => {
                        dispatch(toggleAssignModal())
                        dispatch(toggleBool())
                    },
                })
            )
        },
    })

    const details_res = async (res) => {
        try {
            if (res?.status === 200) {
                const data = res?.data?.data
                await Promise.all([
                    dispatch(premiseUserList({ pageable: { premise_id: data?.premise_id, role_id: 8 }, navigate })),
                ])
                setLoading(false)
                setBreakLoading(false)
            } else {
                setBreakLoading(false)
            }
        } catch {
            setBreakLoading(false)
        }
    }

    useEffect(() => {
        setLoading(true)
        setBreakLoading(true)
        dispatch(removePremiseUser())
        dispatch(ticketDetails({ id, navigate })).then((res) => details_res(res.payload))
    }, [])

    if (loading && breakLoading) return <Loader />
    if (loading && !breakLoading)
        return (
            <div className="layout">
                <div className="text-center text-muted">
                    <AlertCircle size={20} className="me-2" />
                    {t('common.wrong')}
                </div>
            </div>
        )

    const incidentStatus = details?.incident_status
    const priority = details?.incident_priority
    const assignedToName = userDisplay(details?.assignedToUserInfo)

    return (
        <Form onSubmit={(e) => { e.preventDefault(); formik.handleSubmit() }}>
            <div className="ticket-assign-wrap">

                <div className="ticket-assign-card">
                    <div className="ticket-assign-card-header">
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            <h6 className="ticket-assign-title mb-0">
                                {details?.incident_title || '—'}
                            </h6>
                            {incidentStatus && (
                                <span className={`${STATUS_CLASS[incidentStatus] || 'badge bg-secondary'} ticket-assign-badge`}>
                                    {incidentStatus}
                                </span>
                            )}
                            {priority && (
                                <span className={`${PRIORITY_CLASS[priority] || 'badge bg-secondary'} ticket-assign-badge`}>
                                    {priority}
                                </span>
                            )}
                        </div>
                        <div className="ticket-assign-code">
                            <Hash size={12} /> {details?.incident_code || '—'}
                        </div>
                    </div>

                    <div className="ticket-assign-meta-grid">
                        <div className="ticket-assign-meta-item">
                            <div className="ticket-assign-meta-icon"><Building2 size={13} /></div>
                            <div>
                                <div className="ticket-assign-meta-label">{t('ticket.premise')}</div>
                                <div className="ticket-assign-meta-value">{details?.premiseInfo?.name || '—'}</div>
                            </div>
                        </div>

                        <div className="ticket-assign-meta-item">
                            <div className="ticket-assign-meta-icon"><MapPin size={13} /></div>
                            <div>
                                <div className="ticket-assign-meta-label">{t('ticket.unit')}</div>
                                <div className="ticket-assign-meta-value">{details?.unitInfo?.unit_number || '—'}</div>
                            </div>
                        </div>

                        <div className="ticket-assign-meta-item">
                            <div className="ticket-assign-meta-icon"><Flag size={13} /></div>
                            <div>
                                <div className="ticket-assign-meta-label">{t('ticket.table_category')}</div>
                                <div className="ticket-assign-meta-value">
                                    {details?.complaintCategoryInfo?.complaint_category_name || '—'}
                                </div>
                            </div>
                        </div>

                        <div className="ticket-assign-meta-item">
                            <div className="ticket-assign-meta-icon"><FileText size={13} /></div>
                            <div>
                                <div className="ticket-assign-meta-label">{t('ticket.table_complaint_type')}</div>
                                <div className="ticket-assign-meta-value">{details?.incident_type || '—'}</div>
                            </div>
                        </div>
                    </div>

                    <div className="ticket-assign-current">
                        <UserCheck size={14} className="text-secondary" />
                        <span className="ticket-assign-current-label">
                            {t('ticket.currently_assigned_to')}:
                        </span>
                        <span className="ticket-assign-current-value">
                            {userDisplay(details?.assignedTo) || (
                                <span className="text-muted fst-italic">{t('ticket.no_assignment_yet')}</span>
                            )}
                        </span>
                    </div>
                </div>

                <div className="ticket-assign-form-section">
                    <div className="ticket-assign-form-heading">
                        <UserPlus size={14} className="text-primary" />
                        <span>{t('ticket.assign_ticket_subtitle')}</span>
                    </div>

                    <SelectBox
                        label_name={t('ticket.assign_to_user')}
                        mandatory={true}
                        placeholder={''}
                        loading={userLoading}
                        option={user_option}
                        customOption={userOptionRender}
                        name="user_id"
                        value={formik.values.user_id}
                        onChange={(e) => formik.setFieldValue('user_id', e)}
                        onBlur={() => formik.setFieldTouched('user_id', true)}
                        onClear={true}
                        filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        error={
                            formik.errors.user_id && formik.touched.user_id ? (
                                <span className="field_error">{formik.errors.user_id}</span>
                            ) : null
                        }
                    />
                </div>

                <div className="d-flex justify-content-end mt-3">
                    <div className="col-lg-4 col-md-5 col-sm-6">
                        <SaveButton button_name={t('common.submit')} spinner={spinner} />
                    </div>
                </div>
            </div>
        </Form>
    )
}